import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { Badge, ErrorState, LoadingState, PageHeader } from '@/components/primitives';
import { adminApi, formatUtc, toastError, useAdminQuery, useBusy } from '@/pages/admin/adminApi';
import { ago } from '@/pages/dashboard/format';
import { TypeBadge } from '@/pages/actors/actorUi';
import '@/styles/actors.css';

/*
 * Analyst console for threat actor profiles: what needs review (suggestions from reporting),
 * actors our stories name that have no profile yet, and every profile with its status.
 */

const VIEWS = [
  { id: 'review', label: 'Needs review' },
  { id: 'unprofiled', label: 'From stories, no profile' },
  { id: 'all', label: 'All profiles' },
];
const STATUS_TONE = { published: 'success', draft: 'warning', archived: 'neutral' };

function NewProfile({ onCreated, initial }) {
  const [name, setName] = useState(initial?.name || '');
  const [aliases, setAliases] = useState((initial?.aliases || []).join(', '));
  const { run, busy } = useBusy();
  useEffect(() => { setName(initial?.name || ''); setAliases((initial?.aliases || []).join(', ')); }, [initial]);
  const submit = (e) => {
    e.preventDefault();
    run('create', async () => {
      try {
        const res = await adminApi.post('/admin/actors', { name: name.trim(), aliases: aliases.split(',').map((a) => a.trim()).filter(Boolean) });
        toast.success(`${name.trim()} created as a draft`);
        onCreated(res.data.profile.id);
      } catch (err) {
        const d = err?.response?.data?.detail;
        if (err?.response?.status === 409 && d?.id) {
          toast.error('A profile already covers this name', { description: 'Opening it instead.' });
          onCreated(d.id);
        } else toastError(err, "Couldn't create the profile");
      }
    });
  };
  return (
    <form className="ac-card ac-new" onSubmit={submit}>
      <h2 className="ac-h2">New profile</h2>
      <div className="ac-new-row">
        <label className="ac-field"><span>Name</span><input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required placeholder="Silent Lynx" data-testid="new-actor-name" /></label>
        <label className="ac-field ac-field--grow"><span>Aliases, comma separated</span><input value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="TAG-193, YoroTrooper" /></label>
        <button type="submit" className="is-btn is-btn--primary" disabled={busy('create') || name.trim().length < 2}>{busy('create') ? 'Creating…' : 'Create draft'}</button>
      </div>
      <p className="ac-fine">New profiles start as drafts. Customers see a profile once you publish it. Stories that name the actor or an alias link to it automatically.</p>
    </form>
  );
}

function Unprofiled({ rows, profiles, onCreate, onLinked }) {
  const [target, setTarget] = useState({});
  const { run, busy } = useBusy();
  const options = useMemo(() => profiles.map((p) => ({ id: p.id, label: `${p.name}${p.mitre?.id ? ` (${p.mitre.id})` : ''}` })), [profiles]);
  const link = (row) => run(row.key, async () => {
    const choice = options.find((o) => o.label === target[row.key]);
    if (!choice) { toast.error('Choose a profile from the list'); return; }
    try {
      const cur = await adminApi.get(`/admin/actors/${choice.id}`);
      const existing = cur.data.layers.curated.aliases || [];
      await adminApi.put(`/admin/actors/${choice.id}`, { aliases: [...existing, row.name, ...row.aliases] });
      toast.success(`${row.name} is now an alias of ${choice.label}`, { description: 'Its stories now count toward that profile.' });
      onLinked();
    } catch (err) {
      toastError(err, "Couldn't add the alias");
    }
  });
  if (!rows.length) return <div className="ac-empty"><b>Every actor in our stories has a profile</b><span>New names appear here when the analysis attributes a story to an actor no profile covers.</span></div>;
  return (
    <>
      <datalist id="actor-profiles">{options.map((o) => <option key={o.id} value={o.label} />)}</datalist>
      <ul className="ac-unprof">
        {rows.map((r) => (
          <li key={r.key} className="ac-card" data-testid={`unprofiled-${r.key}`}>
            <div className="ac-card-h">
              <div>
                <h3 className="ac-sw-name">{r.name}</h3>
                {r.aliases.length > 0 && <p className="ac-fine">Also named {r.aliases.join(', ')}</p>}
              </div>
              <span className="ac-muted">{r.story_count} {r.story_count === 1 ? 'story' : 'stories'}, last {ago(r.last_reported)}</span>
            </div>
            <ul className="ac-unprof-stories">{r.stories.map((s) => <li key={s.id}>{s.name}</li>)}</ul>
            <div className="ac-unprof-actions">
              <button type="button" className="is-btn is-btn--primary is-btn--sm" onClick={() => onCreate(r)}><Plus aria-hidden="true" /> Create a profile</button>
              <span className="ac-muted">or the same actor as</span>
              <input list="actor-profiles" className="ac-input-sm" placeholder="Find a profile" value={target[r.key] || ''} onChange={(e) => setTarget((t) => ({ ...t, [r.key]: e.target.value }))} aria-label={`Existing profile for ${r.name}`} />
              <button type="button" className="is-btn is-btn--secondary is-btn--sm" disabled={busy(r.key) || !target[r.key]} onClick={() => link(r)}>Add as alias</button>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

export default function AdminActors() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = VIEWS.some((v) => v.id === params.get('view')) ? params.get('view') : 'review';
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [draft, setDraft] = useState(null);
  const res = useAdminQuery(() => adminApi.get('/admin/actors').then((r) => r.data), []);
  const { run, busy } = useBusy();
  const d = res.data;

  // While a sync runs, poll its status so the page reports the outcome.
  useEffect(() => {
    if (!d?.sync?.running) return undefined;
    const t = setInterval(res.reload, 4000);
    return () => clearInterval(t);
  }, [d?.sync?.running, res.reload]);

  const setView = (v) => setParams((p) => { const n = new URLSearchParams(p); if (v === 'review') n.delete('view'); else n.set('view', v); return n; }, { replace: true });
  const actors = useMemo(() => d?.actors || [], [d]);
  const review = actors.filter((a) => a.pending_suggestions > 0);
  const all = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return actors.filter((a) => (!status || a.status === status)
      && (!needle || [a.name, ...a.aliases, a.mitre?.id || ''].join(' ').toLowerCase().includes(needle)))
      .sort((x, y) => x.name.localeCompare(y.name));
  }, [actors, q, status]);

  const sync = () => run('sync', async () => {
    try {
      await adminApi.post('/admin/actors/sync-attack');
      toast.success('ATT&CK sync started', { description: 'Profiles keep every analyst edit; only the ATT&CK layer is refreshed.' });
      res.reload();
    } catch (err) {
      toastError(err, "Couldn't start the sync");
    }
  });

  const table = (rows, showSuggestions) => (
    <div className="ac-table-wrap">
      <table className="ac-table">
        <thead>
          <tr>
            <th scope="col">Profile</th><th scope="col">Status</th><th scope="col">Type</th>
            {showSuggestions && <th scope="col" className="ac-num">Suggestions</th>}
            <th scope="col" className="ac-num">Stories</th><th scope="col">Edited by analysts</th><th scope="col">Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} onClick={() => navigate(`/admin/actors/${a.id}`)} data-testid={`admin-actor-${a.slug}`}>
              <td className="ac-td-name">
                <Link to={`/admin/actors/${a.id}`} onClick={(e) => e.stopPropagation()}>{a.name}</Link>
                <span className="ac-aliases">{a.mitre?.id ? `${a.mitre.id} · ` : 'Analyst profile · '}{a.aliases.slice(0, 3).join(', ')}</span>
              </td>
              <td><Badge size="sm" tone={STATUS_TONE[a.status]}>{a.status}</Badge></td>
              <td><TypeBadge type={a.type} label={a.type_label} /></td>
              {showSuggestions && <td className="ac-num"><b>{a.pending_suggestions}</b></td>}
              <td className="ac-num">{a.reporting.attributed}{a.reporting.mentioned ? <span className="ac-muted"> +{a.reporting.mentioned}</span> : ''}</td>
              <td className="ac-td-targets">{a.curated_fields.length ? a.curated_fields.slice(0, 4).join(', ') + (a.curated_fields.length > 4 ? '…' : '') : <span className="ac-muted">Not yet</span>}</td>
              <td className="ac-nowrap">{ago(a.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="pg ac" data-testid="admin-actors">
      <PageHeader
        title="Threat actor profiles"
        subtitle="Review what reporting suggests, profile actors our stories name, and edit any profile. Analyst edits always win over ATT&CK and are kept through every sync."
        actions={(
          <>
            <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={sync} disabled={busy('sync') || d?.sync?.running}>
              <RefreshCw className={d?.sync?.running ? 'is-spinner' : undefined} aria-hidden="true" /> {d?.sync?.running ? 'Syncing ATT&CK…' : 'Sync ATT&CK'}
            </button>
            <button type="button" className="is-btn is-btn--primary is-btn--sm" onClick={() => setDraft({ name: '', aliases: [] })}><Plus aria-hidden="true" /> New profile</button>
          </>
        )}
      />

      {res.loading && !d ? <LoadingState label="profiles" rows={8} />
        : res.error && !d ? <ErrorState title="Couldn't load profiles" error={res.error} onRetry={res.reload} />
          : (
            <>
              <section className="is-stats" style={{ '--is-stats-n': 5 }} aria-label="Key numbers">
                <div className="is-stats-item"><span className="is-stats-l">Profiles</span><b className="is-stats-v">{actors.length}</b><span className="is-stats-s">{actors.filter((a) => a.status === 'published').length} published</span></div>
                <div className="is-stats-item"><span className="is-stats-l">Suggestions to review</span><b className="is-stats-v" data-tone={review.length ? 'high' : undefined}>{review.reduce((n, a) => n + a.pending_suggestions, 0)}</b><span className="is-stats-s">On {review.length} profiles</span></div>
                <div className="is-stats-item"><span className="is-stats-l">Named in stories, no profile</span><b className="is-stats-v">{d.unprofiled.length}</b><span className="is-stats-s">Create or link as alias</span></div>
                <div className="is-stats-item"><span className="is-stats-l">Edited by analysts</span><b className="is-stats-v">{actors.filter((a) => a.curated_fields.length).length}</b><span className="is-stats-s">Profiles with analyst changes</span></div>
                <div className="is-stats-item"><span className="is-stats-l">ATT&CK</span><b className="is-stats-v">{d.attack?.version ? `v${d.attack.version}` : 'Not synced'}</b><span className="is-stats-s">{d.attack?.synced_at ? `Synced ${formatUtc(d.attack.synced_at)}` : 'Run a sync to load groups'}</span></div>
              </section>
              {d.sync?.error && <p className="ac-warn">The last ATT&CK sync failed: {d.sync.error}</p>}
              {d.sync?.last && !d.sync.running && <p className="ac-fine">Last sync: {d.sync.last.created} new, {d.sync.last.updated} refreshed, {d.sync.last.withdrawn} withdrawn by ATT&CK.</p>}

              {draft && <NewProfile initial={draft} onCreated={(id) => navigate(`/admin/actors/${id}`)} />}

              <div className="is-seg" role="group" aria-label="View">
                {VIEWS.map((v) => (
                  <button key={v.id} type="button" aria-pressed={view === v.id} onClick={() => setView(v.id)} data-testid={`admin-view-${v.id}`}>
                    {v.label} <span className="ac-count-n">{v.id === 'review' ? review.length : v.id === 'unprofiled' ? d.unprofiled.length : actors.length}</span>
                  </button>
                ))}
              </div>

              {view === 'review' && (review.length ? table(review, true)
                : <div className="ac-empty"><b>Nothing to review</b><span>Suggestions appear when a story attributed to a profiled actor shows a technique, malware, country or sector the profile does not have.</span></div>)}
              {view === 'unprofiled' && <Unprofiled rows={d.unprofiled} profiles={actors} onCreate={(r) => { setDraft({ name: r.name, aliases: r.aliases }); window.scrollTo({ top: 0, behavior: 'smooth' }); }} onLinked={res.reload} />}
              {view === 'all' && (
                <>
                  <div className="ac-toolbar">
                    <label className="ac-search"><Search aria-hidden="true" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, alias or ATT&CK ID" aria-label="Search profiles" /></label>
                    <label className="ac-filter"><select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                      <option value="">Status: All</option><option value="published">Published</option><option value="draft">Draft</option><option value="archived">Archived</option>
                    </select></label>
                  </div>
                  {table(all, false)}
                </>
              )}
            </>
          )}
    </div>
  );
}
